// The journey core every mode shares (#1317, the shared journey core's step 1; ba-web 5987146358): the stage model and the
// store-free helpers that Explore › Powers of Ten built (features/journey.ts, #792) and that Body's dives (heart-to-filament,
// one-heartbeat, the Walk) use over the same `journey` reply and events. Moved here verbatim; features/journey.ts re-exports them.
// Rules, as src/suite/modes.ts: types only from the viewport API (it has runtime exports, and a value import would pull it into a
// mode's chunk), no store, no import from app/, features/ or zustand, and nothing happens at import time. A mode's own words
// (captions, notes, Methods) and its store, bound to its clock and viewport, stay in the mode.

import type { JourneyReply, JourneyStage } from '../viewport/api.ts';

export type { JourneyReply, JourneyStage };

export const reason = (err: unknown): string => (err instanceof Error ? err.message : String((err as { error?: unknown } | null)?.error ?? err));

/** The journey's reply as the viewport sends it, or why it isn't one. */
export function asReply(r: unknown): JourneyReply {
  const x = r as Partial<JourneyReply> | null;
  if (!x || typeof x.durationS !== 'number' || !Array.isArray(x.stages) || !x.stages.length) throw new Error('the viewport’s reply names no stages');
  return { durationS: x.durationS, stages: x.stages, data: Array.isArray(x.data) ? x.data : [], scale: typeof x.scale === 'string' ? x.scale : '' };
}

/** The stage a moment falls in (the last stage keeps its end), or null outside the journey. */
export function stageAt(reply: Pick<JourneyReply, 'stages'> | null, t: number): string | null {
  if (!reply) return null;
  const last = reply.stages.at(-1);
  if (last && t >= last.t1) return last.id;
  return reply.stages.find((st) => t >= st.t0 && t < st.t1)?.id ?? null;
}

/** The view's height at a moment, from the reply: log-interpolated inside its stage, as the ruler interpolates (spec §2). */
export function scaleAt(reply: Pick<JourneyReply, 'stages'> | null, t: number): number | null {
  const id = stageAt(reply, t);
  const st = reply?.stages.find((x) => x.id === id);
  if (!st) return null;
  const f = st.t1 > st.t0 ? Math.min(1, Math.max(0, (t - st.t0) / (st.t1 - st.t0))) : 0;
  return st.scaleMm[0] * (st.scaleMm[1] / st.scaleMm[0]) ** f;
}

/** A scale as the ruler reads it: mm to three figures, µm below a millimetre, nm below a micrometre (#928's synapse and
 *  receptor), as the scale bar does (features/scaleBar.ts). */
export function formatScale(mm: number): string {
  if (mm >= 1) return `${+mm.toPrecision(3)} mm`;
  return mm >= 1e-3 ? `${+(mm * 1e3).toPrecision(3)} µm` : `${+(mm * 1e6).toPrecision(3)} nm`;
}

/** Where a scale sits on a log ruler from `hi` (top, 0) to `lo` (bottom, 1). */
export function rulerFrac(scaleMm: number, hi: number, lo: number): number {
  if (!(hi > lo) || !(scaleMm > 0)) return 0;
  return Math.min(1, Math.max(0, Math.log(hi / scaleMm) / Math.log(hi / lo)));
}

/** The decades inside the ruler's range (100 mm, 10 mm, 1 mm, on down to 10 nm), largest first. */
export function rulerDecades(hi: number, lo: number): number[] {
  const out: number[] = [];
  for (let e = 3; e >= -6; e--) {
    const v = 10 ** e;
    if (v < hi && v > lo) out.push(v);
  }
  return out;
}

/**
 * Which stops carry their names on a ruler `heightPx` tall: the stage on screen (`current`) first, whose name always
 * shows, then the longer stages, and a name only where no other shown name is within `minGapPx`. The dissolves
 * (0.8 s, a few per cent of the ruler) keep their dot, and so do the top end's stops beside the stage on screen:
 * the head's four come 10–14 px apart on a laptop's ruler.
 */
export function namedStops(stages: JourneyStage[], hi: number, lo: number, heightPx: number, current: string | null = null, minGapPx = 16): Set<string> {
  const shown = new Set<string>();
  const taken: number[] = [];
  const ordered = [...stages].sort((a, b) => Number(b.id === current) - Number(a.id === current) || b.t1 - b.t0 - (a.t1 - a.t0) || a.t0 - b.t0);
  for (const st of ordered) {
    const y = rulerFrac(st.scaleMm[0], hi, lo) * heightPx;
    if (taken.some((t) => Math.abs(t - y) < minGapPx)) continue;
    taken.push(y);
    shown.add(st.id);
  }
  return shown;
}

/** The provenance word a stage's Methods line ends in (" · reference", " · reference, measured"). */
export function provenanceOf(methods: string): string | null {
  const i = methods.lastIndexOf(' · ');
  return i > 0 ? methods.slice(i + 3).trim() : null;
}
